import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ROUTES } from '../../app/routes';
import axios from 'axios';

export interface OrgChartEmployee {
  EmployeeNumber: string;
  EmployeeFullName: string;
  WorkEmail: string;
  JobTitle: string;
  HierarchyPath: string;
  direct_reportees: number;
  total_reportees: number;
  EmpOpenReqs: number;
}

export interface OrgChartDirect {
  EmployeeNumber: string;
  EmployeeFullName: string;
  WorkEmail: string;
  JobTitle: string;
  direct_reportees: number;
  total_reportees: number;
  EmpOpenReqs: number;
}

/** Fetch a single employee from the OrgChart PostgreSQL (bigquerydata) */
export const fetchOrgChartEmployee = createAsyncThunk(
  'orgChart/fetchOrgChartEmployee',
  async (employeeNumber: string, { rejectWithValue }) => {
    try {
      const response = await axios({
        method: 'get',
        url: `${ROUTES.getOrgChartEmployee}?employeeNumber=${encodeURIComponent(employeeNumber)}`,
        withCredentials: true,
      });
      if (response.data?.success && response.data?.data) {
        return response.data.data as OrgChartEmployee;
      }
      return rejectWithValue(response.data?.message || 'Employee not found');
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch OrgChart employee');
    }
  },
);

/** Fetch direct reportees of a supervisor from the OrgChart PostgreSQL (bigquerydata) */
export const fetchOrgChartDirects = createAsyncThunk(
  'orgChart/fetchOrgChartDirects',
  async (supervisorEmployeeNumber: string, { rejectWithValue }) => {
    try {
      const response = await axios({
        method: 'get',
        url: `${ROUTES.getOrgChartDirects}?supervisorEmployeeNumber=${encodeURIComponent(supervisorEmployeeNumber)}`,
        withCredentials: true,
      });
      if (response.data?.success) {
        return response.data.data as OrgChartDirect[];
      }
      return rejectWithValue(response.data?.message || 'Failed to fetch directs');
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch OrgChart directs');
    }
  },
);

// ─── Slice ──────────────────────────────────────────────────────────────────

interface OrgChartState {
  employee: OrgChartEmployee | null;
  directs: OrgChartDirect[];
  employeeStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  directsStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  employeeError: string | null;
  directsError: string | null;
}

const initialState: OrgChartState = {
  employee: null,
  directs: [],
  employeeStatus: 'idle',
  directsStatus: 'idle',
  employeeError: null,
  directsError: null,
};

const orgChartSlice = createSlice({
  name: 'orgChart',
  initialState,
  reducers: {
    resetOrgChart: (state) => {
      state.employee = null;
      state.directs = [];
      state.employeeStatus = 'idle';
      state.directsStatus = 'idle';
      state.employeeError = null;
      state.directsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchOrgChartEmployee
      .addCase(fetchOrgChartEmployee.pending, (state) => {
        state.employeeStatus = 'loading';
        state.employeeError = null;
      })
      .addCase(fetchOrgChartEmployee.fulfilled, (state, action) => {
        state.employeeStatus = 'succeeded';
        state.employee = action.payload;
      })
      .addCase(fetchOrgChartEmployee.rejected, (state, action) => {
        state.employeeStatus = 'failed';
        state.employeeError = action.payload as string;
      })
      // fetchOrgChartDirects
      .addCase(fetchOrgChartDirects.pending, (state) => {
        state.directsStatus = 'loading';
        state.directsError = null;
      })
      .addCase(fetchOrgChartDirects.fulfilled, (state, action) => {
        state.directsStatus = 'succeeded';
        state.directs = action.payload;
      })
      .addCase(fetchOrgChartDirects.rejected, (state, action) => {
        state.directsStatus = 'failed';
        state.directsError = action.payload as string;
      });
  },
});

export const { resetOrgChart } = orgChartSlice.actions;
export default orgChartSlice.reducer;
